import _AccountProfileSetLanguageMutation from "../__generated__/AccountProfileSetLanguageMutation.graphql";
import _AccountProfileUpdateMutation from "../__generated__/AccountProfileUpdateMutation.graphql";
import _AccountProfileQuery from "../__generated__/AccountProfileQuery.graphql";
import { useCallback, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Link } from 'react-router-dom';
import type { AccountProfileQuery } from '../__generated__/AccountProfileQuery.graphql';
import type { AccountProfileSetLanguageMutation } from '../__generated__/AccountProfileSetLanguageMutation.graphql';
import type { AccountProfileUpdateMutation } from '../__generated__/AccountProfileUpdateMutation.graphql';
import type { guardsMeQuery } from '../__generated__/guardsMeQuery.graphql';
import { Field, LanguageField, LevelField } from '../components/Field.tsx';
import { MeQuery } from '../components/guards';
import { LanguagePicker } from '../components/LanguagePicker';
import { LearnerPicture } from '../components/LearnerPicture';
import { useLocale } from '../i18n/LocaleProvider';
import { isShipped, type ShippedLocale } from '../i18n/locale';
import { msg, t } from '../i18n/t';
import { formFor, isCefrLevel, MAX_DISPLAY_NAME_CHARS, profileBody, validateProfile, type CefrLevel, type FieldError, type FieldName, type ProfileForm } from '../profile/profile';
import { showMemoriesCard } from '../profile/memories';
import { registerFor, type Register } from '../profile/register';
import { codeFromError, copyFor } from './aidErrors.ts';
import { MemoriesCard } from './MemoriesCard.tsx';
import { PictureDialog } from './PictureDialog.tsx';
const AccountProfileQuery = (_AccountProfileQuery.hash && _AccountProfileQuery.hash !== "37d1ba7816776abe0421f04f4ed5aa3e" && console.error("The definition of 'AccountProfileQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _AccountProfileQuery);

/**
 * `id` beside the changed fields is what makes this an update rather than an
 * insert: Relay keys its normalized store on it, so every other screen reading
 * the same learner refreshes from this one response.
 *
 * `SessionGate` records what the other shape costs. `endSession` selects only
 * `id` and `status`, so nothing invalidated `lastSession` and that screen had
 * to be forced to `network-only` or it would send a learner back into the
 * conversation they had just finished.
 */
const UpdateLearner = (_AccountProfileUpdateMutation.hash && _AccountProfileUpdateMutation.hash !== "11263b7138282d92b9b8a59aa2ec1d4c" && console.error("The definition of 'AccountProfileUpdateMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _AccountProfileUpdateMutation);
const SetLanguage = (_AccountProfileSetLanguageMutation.hash && _AccountProfileSetLanguageMutation.hash !== "b0abb5e8be2797042ed86b4614f747f9" && console.error("The definition of 'AccountProfileSetLanguageMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _AccountProfileSetLanguageMutation);

/**
 * Applied the moment it is chosen, not by the form's Save below: this is how
 * someone who cannot read the current language gets out of it, and a Save
 * button labelled in that language would defeat it. The server's answer is
 * what switches the page, so the page never shows a language it did not store.
 */
function DisplayLanguage() {
  const {
    locale,
    choose
  } = useLocale();
  const [commit, inFlight] = useMutation<AccountProfileSetLanguageMutation>(SetLanguage);
  const [failed, setFailed] = useState(false);
  const change = (language: ShippedLocale) => {
    setFailed(false);
    commit({
      variables: {
        language
      },
      onCompleted: res => {
        const next = res.setInterfaceLanguage.learner?.interfaceLocale;
        if (next && isShipped(next)) {
          void choose(next).then(loaded => {
            if (!loaded) setFailed(true);
          });
        }
      },
      onError: () => setFailed(true)
    });
  };
  return <div className="card bg-base-100 shadow-sm">
      <div className="card-body p-5 sm:p-8">
        <LanguagePicker value={locale === 'qps' ? 'en' : locale} onChange={change} disabled={inFlight} error={failed ? t('language-failed') : null} />
      </div>
    </div>;
}
type ProfileLearner = NonNullable<NonNullable<AccountProfileQuery['response']['me']>['learner']>;

/**
 * Its own card rather than a form field (learner picture PRD §3.2): the
 * dialog applies a choice the moment it is made, so the form's Save never
 * has to say which of two unrelated things failed.
 */
function PictureCard({
  learner,
  register
}: {
  learner: ProfileLearner;
  register: Register;
}) {
  const [open, setOpen] = useState(false);
  return <div className="card bg-base-100 shadow-sm">
      <div className="card-body p-5 sm:p-8 flex-row items-center gap-6">
        <LearnerPicture picture={learner.picture} displayName={learner.displayName} learnerId={learner.id} size={128} />
        <div className="flex flex-col gap-3">
          <h2 className="card-title">{t('picture-dialog-title')}</h2>
          <button type="button" className="btn btn-outline min-h-(--hit-min) self-start" onClick={() => setOpen(true)}>
            {t('picture-change')}
          </button>
        </div>
      </div>
      {open && <PictureDialog picture={learner.picture} displayName={learner.displayName} learnerId={learner.id} register={register} onClose={() => setOpen(false)} />}
    </div>;
}

/** A fact about the account, rather than a control someone turned off. */
function ReadOnlyRow({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex items-baseline justify-between gap-4 py-2">
      <span className="fieldset-label">{label}</span>
      <span className="font-medium">{value}</span>
    </div>;
}
export function AccountProfile() {
  const data = useLazyLoadQuery<AccountProfileQuery>(AccountProfileQuery, {});
  const account = data.me;

  // The query the guards already ran, so Relay serves it from the store, as
  // `AppLayout` reads it.
  const me = useLazyLoadQuery<guardsMeQuery>(MeQuery, {});
  const register = registerFor(me.me?.learner?.defaultCharacter?.audience);
  const [form, setForm] = useState<ProfileForm>(() => formFor(account?.learner));
  // What the server last said was stored, sent as `previousCefrLevel` so a
  // form opened before somebody else changed the level cannot undo them.
  const [storedLevel, setStoredLevel] = useState<CefrLevel | null>(() => formFor(account?.learner).cefrLevel);
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [saved, setSaved] = useState(false);
  const [commit, inFlight] = useMutation<AccountProfileUpdateMutation>(UpdateLearner);
  const errorFor = (field: FieldName) => errors.find(e => e.field === field)?.message;
  const formErrors = errors.filter(e => e.field === null);

  // Every edit clears "Saved.", so the word never describes a form that has
  // since been changed.
  const update = useCallback((patch: Partial<ProfileForm>) => {
    setSaved(false);
    setForm(f => ({
      ...f,
      ...patch
    }));
  }, []);
  const save = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const found = validateProfile(form);
    setErrors(found);
    if (found.length > 0) return;
    commit({
      variables: {
        input: profileBody(form, storedLevel)
      },
      // `updateLearner` refuses by returning a GraphQL error, not an `ok`
      // field. `UpdateLearnerPayload!` is non-null, so relay-runtime nulls
      // `data` and throws rather than calling `onCompleted` with errors
      // beside a response (verified against relay-runtime 21.0.1's
      // `OperationExecutor.js`, the same reasoning `aidErrors.ts` documents
      // for `translateTurn`/`speak`) -- every refusal, including ruling Y's
      // new one, lands here.
      onCompleted: res => {
        const level = res.updateLearner.learner?.cefrLevel;
        if (isCefrLevel(level)) setStoredLevel(level);
        setSaved(true);
      },
      onError: error => {
        const code = codeFromError(error);
        // `CONFLICT` here is only ever the level guard. `copyFor` is shared
        // with the aids, which never send it, so the sentence lives here.
        const message = t(code === 'CONFLICT' ? msg('profile-level-conflict') : copyFor(code));
        setErrors([{
          field: null,
          message
        }]);
      }
    });
  }, [commit, form, storedLevel]);
  return <div className="flex flex-col gap-6">
      {account?.learner && <PictureCard learner={account.learner} register={register} />}
      {account?.learner && register === 'adult' && showMemoriesCard(register, account.learner.memories.length) && <MemoriesCard memories={account.learner.memories} />}
      {register === 'kid' && <Link to="/account/history" className="link link-primary text-sm">
          {t('grown-ups-memories-moved')}
        </Link>}
      <DisplayLanguage />
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body p-5 sm:p-8">
          <form onSubmit={save} className="flex flex-col gap-1">
            <Field id="displayName" label={t('profile-display-name')} error={errorFor('displayName')} control={props => <input {...props} className={`${props.className} input`} value={form.displayName} onChange={e => update({
            displayName: e.target.value
          })} maxLength={MAX_DISPLAY_NAME_CHARS} />} />

            <LanguageField choice={form.languageChoice} other={form.otherLanguage} error={errorFor('nativeLanguage')} onChoice={v => update({
            languageChoice: v
          })} onOther={v => update({
            otherLanguage: v
          })} />

            <LevelField value={form.cefrLevel} register={register} onChange={v => update({
            cefrLevel: v
          })} />

            {formErrors.length > 0 && <div className="alert alert-error mt-2" role="alert">
                <ul className="flex flex-col gap-1 text-sm">
                  {formErrors.map(e => <li key={e.message}>{e.message}</li>)}
                </ul>
              </div>}

            {saved && <div className="alert alert-success mt-2" role="status">
                <span className="text-sm">{t('common-saved')}</span>
              </div>}

            <button className="btn btn-primary mt-4 self-start" type="submit" disabled={inFlight}>
              {inFlight ? t('common-saving') : t('common-save')}
            </button>
          </form>
        </div>
      </div>

      {/* The credential half. Read-only here because each of these has its own
          flow in AccountSettings -- and rendered as values rather than as
          `disabled` inputs, which painted a fact in the lowest-contrast
          treatment the theme has for text whose only job is to be read. */}
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body p-5 sm:p-8">
          <ReadOnlyRow label={t('profile-email')} value={account?.email ?? ''} />
          <ReadOnlyRow label={t('profile-email-verified')} value={account?.emailVerified ? t('common-yes') : t('common-no')} />
        </div>
      </div>
    </div>;
}